"use client";

// CompanySearch — the company pages' search, folded INTO the breadcrumb exactly
// like the dashboard's OsSearch and the docs' inline search: a faded "/ search"
// affordance you type into right there, matches dropping into the anchored
// overlay, live as you type. Same chrome classes (.os-crumb-search /
// .os-search-results / .os-search-hit / .os-find-mark) so it is visually
// identical everywhere. The index is whatever posts RLS lets this viewer see —
// fetched once on first focus.

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";

interface PostHit {
  slug: string;
  kind: string;
  title: string;
  dek: string | null;
}

function mark(text: string, q: string): React.ReactNode {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="os-find-mark">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

export default function CompanySearch({ collapsible = false }: { collapsible?: boolean }) {
  const router = useRouter();
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [posts, setPosts] = useState<PostHit[] | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // the index — fetched lazily, once, on first focus
  const ensureIndex = () => {
    if (posts !== null) return;
    setPosts([]);
    fetch("/api/company/posts")
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((d) => setPosts(Array.isArray(d.posts) ? d.posts : []))
      .catch(() => setPosts([]));
  };

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) { setFocused(false); setOpen(false); }
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ensureIndex();
        setOpen(true); setFocused(true);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const term = q.trim().toLowerCase();
  const hits = term.length >= 2 && posts
    ? posts.filter((p) => p.title.toLowerCase().includes(term) || (p.dek ?? "").toLowerCase().includes(term)).slice(0, 8)
    : null;
  const show = focused && hits !== null;
  const placeholder = "search";
  const expanded = !collapsible || open || focused || q.length > 0;

  return (
    <span
      ref={wrapRef}
      data-noswipe
      style={{ display: "inline-flex", alignItems: "baseline", gap: "0.5rem", position: "relative", minWidth: 0 }}
    >
      <span aria-hidden style={{ color: "var(--ink-4)" }}>/</span>
      {collapsible && !expanded ? (
        <button
          type="button"
          className="os-crumb-search-btn"
          aria-label={placeholder}
          onClick={() => { ensureIndex(); setOpen(true); setFocused(true); requestAnimationFrame(() => inputRef.current?.focus()); }}
        >
          <svg width="26" height="26" viewBox="0 0 16 16" fill="none" aria-hidden>
            <circle cx="7" cy="7" r="4.4" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.4 10.4 14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      ) : (
        <input
          ref={inputRef}
          className="os-crumb-search"
          placeholder={placeholder}
          value={q}
          size={Math.max(placeholder.length, q.length + 1)}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { ensureIndex(); setFocused(true); }}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setQ(""); (e.target as HTMLInputElement).blur(); setOpen(false); }
            e.stopPropagation();
          }}
          aria-label="search company posts"
        />
      )}
      {show && (
        <div className="os-search-results" style={{ left: 0, right: "auto", width: "min(34rem, 80vw)", top: "calc(100% + 0.7rem)" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--ink-4)", padding: "0.1rem 0.2rem 0.5rem" }}>on the company page · live</div>
          {hits!.length === 0 && <div style={{ color: "var(--ink-4)", fontSize: "0.85rem", padding: "0.4rem" }}>nothing matches yet.</div>}
          {hits!.map((h) => (
            <button
              key={h.slug}
              className="os-search-hit"
              onClick={() => { router.push(`/company/${h.slug}`); setFocused(false); setOpen(false); setQ(""); }}
            >
              <span style={{ color: "var(--accent-ink)", width: "1.4rem", flexShrink: 0 }}>❋</span>
              <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span style={{ color: "var(--ink)", fontSize: "0.95rem" }}>
                  {mark(h.title, term)} <span style={{ color: "var(--ink-4)", fontSize: "0.78rem" }}>· {h.kind === "update" || h.kind === "inside" ? "inside innernet" : h.kind === "report" ? "reports" : h.kind}</span>
                </span>
                {h.dek && (
                  <span style={{ color: "var(--ink-3)", fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {mark(h.dek, term)}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
